import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, BookOpen, Clock, Trophy, Star } from "lucide-react";
import { api, API, getResults } from "@/lib/api";
import { CATEGORIES } from "@/lib/data";

function CoverImage({ book, size = "normal" }) {
  const [failed, setFailed] = useState(false);
  const [token] = useState(() => localStorage.getItem("life101_user_token"));
  const [blobUrl, setBlobUrl] = useState(null);

  useEffect(() => {
    if (!book.cover_path || failed) return;
    let cancelled = false;
    fetch(`${API}/books/${book.id}/cover`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => (r.ok ? r.blob() : Promise.reject()))
      .then((b) => { if (!cancelled) setBlobUrl(URL.createObjectURL(b)); })
      .catch(() => { if (!cancelled) setFailed(true); });
    return () => { cancelled = true; if (blobUrl) URL.revokeObjectURL(blobUrl); };
  }, [book.id, book.cover_path, token, blobUrl]);

  const cls = size === "small" ? "book-cover w-16" : "book-cover";

  if (book.cover_path && blobUrl && !failed) {
    return (
      <div className={cls} style={{ padding: 0, overflow: "hidden" }}>
        <img src={blobUrl} alt={book.title} className="w-full h-full object-cover" />
      </div>
    );
  }
  return (
    <div className={cls}>
      <div className="text-[10px] uppercase tracking-[0.2em] text-neutral-400">Life 101</div>
      <div className="mt-3 text-base font-bold leading-tight">{book.title.replace(" 101", "").toUpperCase()}</div>
      <div className="text-xs text-neutral-500 mt-1">101</div>
    </div>
  );
}

function BookCard({ book, recommended: isRec }) {
  return (
    <div className="glass p-6 flex flex-col" data-testid={`book-${book.category}`}>
      <span className={`self-start px-3 py-1 rounded-full border text-[10px] uppercase tracking-[0.15em] inline-flex items-center gap-1 ${isRec ? "border-white text-white bg-white/10" : "border-white/12 text-neutral-300"}`}>
        {isRec ? <><Star size={10} /> Sana Önerilen</> : "Life 101 Serisi"}
      </span>
      <div className="grid grid-cols-[110px_1fr] gap-5 mt-5">
        <CoverImage book={book} />
        <div>
          <h3 className="text-xl font-semibold text-white">{book.title}</h3>
          <p className="mt-2 text-sm text-neutral-400 leading-relaxed line-clamp-4">{book.description}</p>
          <div className="mt-4 space-y-2 text-xs text-neutral-400">
            <div className="flex items-center gap-2"><Clock size={14} /> Başlangıç İçin Uygun</div>
            <div className="flex items-center gap-2"><BookOpen size={14} /> {book.pages} sayfa</div>
          </div>
        </div>
      </div>
      <a href={book.shopier_url} target="_blank" rel="noreferrer" className="btn-primary mt-6 inline-flex items-center justify-center gap-2 text-sm" data-testid={`book-buy-${book.category}`}>
        Kitabı İncele <ArrowRight size={14} />
      </a>
    </div>
  );
}

export default function Books() {
  const nav = useNavigate();
  const r = getResults();
  const [books, setBooks] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/books")
      .then((res) => setBooks(res.data.items || []))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="text-center py-24 text-neutral-500">Yükleniyor...</div>;
  if (books.length === 0) {
    return (
      <div className="text-center py-24" data-testid="no-books">
        <p className="text-neutral-400">Henüz kitap eklenmemiş.</p>
      </div>
    );
  }

  const devKeys = r
    ? [...CATEGORIES]
        .map((c) => ({ key: c.key, score: r.scores[c.key] }))
        .sort((a, b) => a.score - b.score)
        .slice(0, 3)
        .map((c) => c.key)
    : [];

  const recommended = devKeys
    .map((k) => books.find((b) => b.category === k))
    .filter(Boolean);
  const recIds = new Set(recommended.map((b) => b.id));
  const others = books.filter((b) => !recIds.has(b.id));
  const firstShopier = books[0]?.shopier_url || "https://www.shopier.com/stenot";

  return (
    <div data-testid="books-page">
      <h1 className="text-5xl md:text-6xl font-semibold tracking-tight h-gradient">Life 101 Kitapları</h1>
      <p className="mt-5 text-neutral-400 max-w-2xl">
        10 yaşam alanı için 10 kitap. {r ? "Sonuçlarına göre en uygun 3 kitabı senin için işaretledik." : "Üye olup testi çözerek sana en uygun 3 kitabı görebilirsin."}
      </p>

      {recommended.length > 0 && (
        <>
          <div className="mt-12 mb-5 flex items-center gap-3">
            <Star size={16} className="text-white" />
            <h2 className="text-xl font-semibold text-white">Sana Uygun 3 Kitap</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {recommended.map((b) => <BookCard key={b.id} book={b} recommended />)}
          </div>
        </>
      )}

      <div className="mt-14 mb-5 flex items-center gap-3">
        <BookOpen size={16} className="text-white" />
        <h2 className="text-xl font-semibold text-white">Tüm Life 101 Kitapları</h2>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {(recommended.length > 0 ? others : books).map((b) => <BookCard key={b.id} book={b} />)}
      </div>

      {/* Bundle */}
      <div className="glass mt-14 p-8 grid grid-cols-1 lg:grid-cols-[200px_1fr_auto] items-center gap-8">
        <div className="flex gap-2">
          {books.slice(0, 3).map((b, i) => (
            <div key={b.id} style={{ transform: `rotate(${(i - 1) * 4}deg)` }}>
              <CoverImage book={b} size="small" />
            </div>
          ))}
        </div>
        <div>
          <h2 className="text-3xl font-semibold text-white">Tüm Life 101 Serisi</h2>
          <p className="mt-2 text-neutral-400 max-w-xl">{books.length} kitap, 10 yaşam alanı için hazırlanmış kapsamlı bir kişisel gelişim serisi.</p>
        </div>
        <a href={firstShopier} target="_blank" rel="noreferrer" className="btn-primary inline-flex items-center gap-2" data-testid="bundle-buy">
          Paketi Gör <ArrowRight size={16} />
        </a>
      </div>

      {recommended.length > 0 && (
        <div className="glass mt-6 p-6 flex flex-wrap items-center gap-6 text-sm text-neutral-300">
          <div className="flex items-center gap-2"><Trophy size={16} /> Senin için önerilen başlangıç sıralaması</div>
          {recommended.map((b, i) => (
            <div key={b.id} className="flex items-center gap-2">
              <span className="w-7 h-7 rounded-full border border-white/20 flex items-center justify-center text-xs">{i + 1}</span>
              <span>{b.title}</span>
              {i < recommended.length - 1 && <ArrowRight size={14} className="text-neutral-600 ml-2" />}
            </div>
          ))}
        </div>
      )}

      <div className="mt-10 text-center">
        <button onClick={() => nav("/kaynaklar")} className="btn-secondary">Ücretsiz Kaynaklara Bak</button>
      </div>
    </div>
  );
}
